import { AdPlacement, Platform } from '@/@types/graphql-types';
import {
	type GetPlacementMutation,
	type GetPlacementMutationVariables,
	GetPlacementDocument,
} from '@/features/ad-server/graphql/mutations/GetPlacement.mutation';
import {
	type ReportMismatchMutation,
	type ReportMismatchMutationVariables,
	ReportMismatchDocument,
} from '@/features/ad-server/graphql/mutations/ReportMismatch.mutation';
import { useMutation } from '@/helpers/composables/useApollo';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { isTitleSeason } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';

import { SwiperRule } from '@/helpers/promotion-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { SnowplowContext, SnowplowFlexContext } from '@/helpers/tracking/providers';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed, onServerPrefetch, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';

import { useUserStore } from '@/stores/pinia/useUserStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { useAdServerStore } from '@/stores/pinia/useAdServerStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import adminConfig from '@/features/promotions/config.json';
import { AdServerCreativeType, isAdServerCreative } from '@/features/ad-server/types';

interface GetAdServerPlacementInput {
	enabled: MaybeRefOrGetter<boolean>;
	title: MaybeRefOrGetter<TitleDetail | null>;
	adPlacement?: AdPlacement;
}

interface VerifyAdServerResultInput {
	placement: MaybeRefOrGetter<'sqtv' | 'qtv' | 'swiper' | 'ft' | null>;
	providers: MaybeRefOrGetter<(string | null | undefined)[] | SwiperRule>;
	enabled: MaybeRefOrGetter<boolean>;
	title: MaybeRefOrGetter<TitleDetail>;
	adPlacement?: AdPlacement;
}

export interface AdServerIds {
	placementCandidateId: number | null;
	bidId: string | null;
	creativeId: number | null;
}

/** Status of the adserver mutation — consumers use this to gate their own fetches */
export type AdServerLoadStatus = 'disabled' | 'loading' | 'loaded' | 'failed';

export function adServerCatalogueKey(placement: AdPlacement, titleId: string) {
	return `${placement}_${titleId}`;
}

const activeCountries: string[] = adminConfig?.ad_server?.countries ?? [];

export function useGetAdServerPlacement(props: GetAdServerPlacementInput) {
	const route = useRoute();
	const languageStore = useLanguageStore();
	const trackingStore = useTrackingStore();
	const adServerStore = useAdServerStore();

	const adPlacement = computed(() => props.adPlacement ?? AdPlacement.Buybox);
	const isRenderedOnServer = computed(() => trackingStore.pagesVisited <= 1);
	const isActiveCountry = computed(() => activeCountries.includes(languageStore.country));
	const title = computed(() => toValue(props.title));
	const enabled = computed(() => toValue(props.enabled) && !!title.value?.id);
	const catalogueKey = computed(() => adServerCatalogueKey(adPlacement.value, title.value?.id ?? ''));
	const mutationVariables = computed(() => {
		if (!title.value) return null;

		const titleSourceId = isTitleSeason(title.value) ? title.value.show.id : title.value.id;

		return {
			country: languageStore.country,
			language: languageStore.language,
			incrementMatchRate: false,
			limit: 1,
			placements: [adPlacement.value],
			platform: Platform.Web,
			url: route.path,
			jwEntityID: titleSourceId,
			entityType: title.value.objectType,
		};
	});

	const { mutate, onError } = useMutation<GetPlacementMutation, GetPlacementMutationVariables>(GetPlacementDocument);

	// Isolated mutation instance for non-active countries
	const { mutate: fireBidOnly, onError: onBidOnlyError } = useMutation<
		GetPlacementMutation,
		GetPlacementMutationVariables
	>(GetPlacementDocument);

	const mutationPromise = ref<Promise<any> | null>(null);

	onError((error: any) => {
		captureMessageForSentry(
			'[GetPlacement Mutation]:',
			{
				error,
				where: 'useGetAdServerPlacement composable',
				country: languageStore.country,
				titleId: title.value?.id,
			},
			'error'
		);
		adServerStore.setStatus(catalogueKey.value, 'failed');
	});

	onBidOnlyError((error: any) => {
		captureMessageForSentry(
			'[GetPlacement Mutation - Bid Only]:',
			{
				error,
				where: 'useGetAdServerPlacement composable (non-active country bid)',
				country: languageStore.country,
				titleId: title.value?.id,
			},
			'error'
		);
	});

	/*
	enabled - an initial guard on the adserver mutation
	title id - used to trigger a mutation when title is updated, or a modal title is rendered
	 */
	watch(
		[enabled, () => title.value?.id],
		([isEnabled, titleId]) => {
			if (!titleId) return;

			const shouldMutate = isEnabled && isActiveCountry.value && (process.client || isRenderedOnServer.value);
			const alreadyFetched = !!adServerStore.placements[catalogueKey.value];

			if (shouldMutate && !alreadyFetched) {
				adServerStore.setStatus(catalogueKey.value, 'loading');
				mutationPromise.value = callAdServerMutation();
			}
		},
		{ immediate: true }
	);

	// Call ad server mutation here (for all countries not in config-countries).
	watch(
		[enabled, () => title.value?.id],
		async ([isEnabled, titleId]) => {
			if (!titleId || !isEnabled) return;
			if (isActiveCountry.value) return;
			if (!process.client) return;
			if (!mutationVariables.value) return;

			const res = await fireBidOnly({
				input: mutationVariables.value,
				country: languageStore.country,
				language: languageStore.language,
				platform: Platform.Web,
				enableCreatives: false,
			});

			const data = res?.data?.getPlacement;
			if (data) adServerStore.setBidOnlyResponse(catalogueKey.value, data);
		},
		{ immediate: true }
	);

	onServerPrefetch(async () => {
		if (!isRenderedOnServer.value) return;
		if (mutationPromise.value) await mutationPromise.value;
	});

	async function callAdServerMutation() {
		if (!mutationVariables.value || !title.value) return;

		const currentKey = catalogueKey.value;
		const res = await mutate({
			input: mutationVariables.value,
			country: languageStore.country,
			language: languageStore.language,
			platform: Platform.Web,
			enableCreatives: isActiveCountry.value,
		});
		const placement = res?.data?.getPlacement;

		if (!placement?.ads?.length) {
			adServerStore.setStatus(currentKey, 'failed');
			return;
		}

		adServerStore.setResponse(currentKey, slimCreativeResponse(placement, title.value));
	}

	// We only want the first two tags which are also present on the source title.
	// To keep the payload small as possible, we handle this here before adding to store and persisting in HTML
	function slimCreativeResponse(
		placement: GetPlacementMutation['getPlacement'],
		sourceTitle: TitleDetail
	): GetPlacementMutation['getPlacement'] {
		// Note for future -> FT and QTV placements can be passed as they are returned, no data slimming should be required
		if (!placement.ads?.some(ad => ad.__typename === AdServerCreativeType.SQTV)) return placement;

		const titleTagNames = new Set((sourceTitle.content.tags ?? []).map(t => t.technicalName));
		return {
			...placement,
			ads: placement.ads.map(ad => {
				if (ad.__typename !== AdServerCreativeType.SQTV) return ad;
				return {
					...ad,
					similarTitle: {
						...ad.similarTitle,
						content: {
							...ad.similarTitle.content,
							tags: (ad.similarTitle.content.tags ?? [])
								.filter(tag => titleTagNames.has(tag.technicalName))
								.slice(0, 2),
						},
					},
				};
			}),
		};
	}

	return { catalogueKey };
}

export function useVerifyAdServerResult(props: VerifyAdServerResultInput) {
	const userStore = useUserStore();
	const languageStore = useLanguageStore();
	const trackingStore = useTrackingStore();
	const adServerStore = useAdServerStore();
	const { mutate: reportMismatch } = useMutation<ReportMismatchMutation, ReportMismatchMutationVariables>(
		ReportMismatchDocument
	);
	const isRenderedOnServer = computed(() => trackingStore.pagesVisited <= 1);
	const adPlacement = computed(() => props.adPlacement ?? AdPlacement.Buybox);
	const enabled = computed(() => toValue(props.enabled));
	const placement = computed(() => toValue(props.placement));
	const providers = computed(() => toValue(props.providers));
	const title = computed(() => toValue(props.title));
	const isActiveCountry = computed(() => activeCountries.includes(languageStore.country));
	const key = computed(() => adServerCatalogueKey(adPlacement.value, title.value.id));
	const storeKey = computed(() => (isActiveCountry.value ? 'placements' : 'bidOnlyPlacements'));

	const expectedResult = computed(() => {
		if (userStore.isProUser || placement.value == null) return null;
		return { placement: placement.value, providers: providers.value };
	});

	watch(
		[() => adServerStore[storeKey.value][key.value], expectedResult],
		([entry]) => {
			// prevent reporting mismatch on server requested placements
			// active country -> first page is server rendered
			// bid only -> all bids are client only
			if (!process.client || (isActiveCountry.value && isRenderedOnServer.value)) return;
			if (!entry?.data || entry.responseVerified || !enabled.value) return;
			adServerStore.setResponseVerified({ key: key.value, storeKey: storeKey.value });
			checkAndReportMismatch(entry.data);
		},
		{ immediate: true }
	);

	function checkAndReportMismatch(data: GetPlacementMutation['getPlacement']) {
		const derived = deriveExpectedResultFromResponse(data);

		if (data?.bidID && hasMismatch(expectedResult.value, derived)) {
			reportMismatch({
				input: {
					bidID: data.bidID,
					expectedResult: JSON.stringify(expectedResult.value),
				},
			});
		}
	}
}

function deriveExpectedResultFromResponse(
	response: GetPlacementMutation['getPlacement'] | null
): { placement: string; provider: string | null } | null {
	const ad = response?.ads?.[0];
	if (!ad) return null;

	switch (ad.__typename) {
		case AdServerCreativeType.SQTV:
			return { placement: 'sqtv', provider: ad.offer.package.shortName };
		case AdServerCreativeType.QTV:
			return { placement: 'qtv', provider: ad.offer.package.shortName };
		case AdServerCreativeType.FREE_TRIAL:
			return { placement: 'ft', provider: ad.offer.package.shortName };
		default:
			return null;
	}
}

function hasMismatch(
	expected: { placement: string; providers: (string | null | undefined)[] | SwiperRule } | null,
	derived: { placement: string; provider: string | null } | null
): boolean {
	if (!expected && !derived) return false; // both null --> match
	if (!expected || !derived) return true; // one null --> mismatch

	// mismatch of placement (always string, easy comparison)
	if (expected.placement !== derived.placement) return true;

	if (!Array.isArray(expected.providers)) {
		if (expected.placement === 'swiper') return true; // SwiperRule - can't currently verify, continue to report mismatch
		return !!derived.provider; // both have non-swiper placement. expected providers is null. If derived provider is not null, mismatch
	}

	const expectedProviders = expected.providers.filter((p): p is string => typeof p === 'string');
	return !expectedProviders.includes(derived.provider || '');
}

export function useAdServer(titleId: MaybeRefOrGetter<string>) {
	const adServerStore = useAdServerStore();
	const languageStore = useLanguageStore();
	const key = computed(() => adServerCatalogueKey(AdPlacement.Buybox, toValue(titleId)));

	const adServerStatus = computed(() => {
		if (!activeCountries.includes(languageStore.country)) return 'disabled';
		return adServerStore.placements[key.value]?.status ?? 'failed';
	});

	const adServerCreative = computed(() => {
		const ad = adServerStore.placements[key.value]?.data?.ads?.[0];
		return isAdServerCreative(ad) ? ad : undefined;
	});

	const adServerIds = computed(() => {
		const response = adServerStore.placements[key.value]?.data;
		if (!response) return null;
		const ad = response.ads?.[0];
		return {
			placementCandidateId: ad?.placementCandidateID ?? null,
			bidId: response.bidID ?? null,
			creativeId: ad?.creativeID ?? null,
		};
	});

	return { adServerStatus, adServerCreative, adServerIds };
}

export type AdServerRenderState = 'rendered' | 'override' | 'disabled';

/* Ad Server Status <> Flex Context
passes some useful information for debugging and snowplow analysis:

ad_server_creative -> 'disabled' | 'loading' | 'loaded' | 'failed'
*an overview of the ad server creative load status*
- disabled: ad server is disabled for the user (e.g. due to country or pro status)
- loading: the mutation has been called, but we haven't received a response
- loaded: we have received a response from the ad server and it contains at least one creative
- failed: we either received an error from the ad server, or we received a response that didn't contain any creatives
   
ad_server_status -> 'rendered' | 'override' | 'disabled'
*an overview of ad server status on page*
- rendered: the ad server creative was rendered on the page
- override: the ad server creative was not rendered because it was overridden (by SR or experiment)
- disabled: the ad server creative was not rendered because the ad server is disabled for this user 
	due to 
	- disabled ad server status (no render attempt was made)
	- returned creative type not being currently available for ad server render e.g. FT, QTV
	- failed request (ad server can return empty creatives array in which case we set status to failed)
*/
export function attachAdServerContexts(
	additionalContexts: SnowplowContext[] = [],
	renderState: AdServerRenderState,
	loadState: AdServerLoadStatus
) {
	return [
		...additionalContexts,
		new SnowplowFlexContext({ data: { ad_server_status: renderState, ad_server_creative: loadState } }),
	];
}
